import { z } from 'zod';
import type { CommissioningBonusMalusEffect, CommissioningBonusMalusWindow, CommissioningThresholdSource } from '../types/commissioning';
import { commissioningThresholdParametersSchema } from './commissioning-threshold-economics';

/**
 * VALORI dei configuratori a struttura/valori (Soglie e Bonus/Malus su KPI)
 * per livello (istanza, cluster, cella driver, membro), keyed sulle identità
 * stabili della struttura del template: `bands` per le bande Soglie,
 * `rules` per le regole Bonus/Malus su KPI. Un livello porta SOLO la
 * sezione del proprio tipo (l'altra resta vuota/assente).
 *
 * Ogni livello salva SOLO i valori che specializza: un campo assente eredita
 * dal livello meno specifico (membro ← cella ← cluster ← istanza). La
 * completezza si valuta sul risultato composto, mai sul singolo livello.
 */
export const commissioningThresholdBandValuesSchema = z.object({
  /** Estremo inferiore della banda (unità dell'indicatore). */
  from: z.number().finite().optional(),
  /** Estremo superiore escluso; assente = banda aperta (solo l'ultima). */
  to: z.number().finite().optional(),
  /** Tolleranza % di ingresso banda (0–99). */
  tolerancePct: z.number().min(0).max(99).optional(),
  canoneMultiplier: z.number().finite().nonnegative().optional(),
  gettoneGaraMultiplier: z.number().finite().nonnegative().optional(),
  perUnitEur: z.number().finite().optional(),
  flatEur: z.number().finite().nonnegative().optional(),
  /** % (0–100) sull'importo netto dell'evento. */
  percentOfAmount: z.number().finite().min(0).max(100).optional(),
  /** Parametri delle categorie della banda (chiave = id categoria). */
  categories: z.record(z.string().uuid(), commissioningThresholdParametersSchema).optional(),
  /** Delta delle maggiorazioni della banda (chiave = id maggiorazione). */
  surcharges: z.record(z.string().uuid(), commissioningThresholdParametersSchema).optional(),
}).strict();
export type CommissioningThresholdBandValues = z.infer<typeof commissioningThresholdBandValuesSchema>;

/** Numeri di una regola «Bonus/Malus su KPI» (la struttura è nel template). */
export const commissioningBonusMalusRuleValuesSchema = z.object({
  /** Estremo inferiore della banda morta (unità del KPI). */
  deadBandLow: z.number().finite().optional(),
  /** Estremo superiore della banda morta. */
  deadBandHigh: z.number().finite().optional(),
  /** Importo applicato sotto la banda morta (solo se la regola dichiara l'effetto malus). */
  malusValue: z.number().finite().optional(),
  /** Importo applicato sopra la banda morta (solo se la regola dichiara l'effetto bonus). */
  bonusValue: z.number().finite().optional(),
}).strict();
export type CommissioningBonusMalusRuleValues = z.infer<typeof commissioningBonusMalusRuleValuesSchema>;
export const COMMISSIONING_BONUS_MALUS_RULE_VALUE_FIELDS = ['deadBandLow', 'deadBandHigh', 'malusValue', 'bonusValue'] as const;
export type CommissioningBonusMalusRuleValueField = typeof COMMISSIONING_BONUS_MALUS_RULE_VALUE_FIELDS[number];

/** Numeri di una fascia di posizione della Classifica (la struttura è nel template). */
export const commissioningRankingPrizeValuesSchema = z.object({
  /** Premio in euro pagato a ogni posizione della fascia. */
  prizeValue: z.number().finite().optional(),
}).strict();
export type CommissioningRankingPrizeValues = z.infer<typeof commissioningRankingPrizeValuesSchema>;
export const COMMISSIONING_RANKING_PRIZE_VALUE_FIELDS = ['prizeValue'] as const;
export type CommissioningRankingPrizeValueField = typeof COMMISSIONING_RANKING_PRIZE_VALUE_FIELDS[number];

/** Numeri della classifica nel suo insieme (facoltativi: assenti = nessun minimo). */
export const commissioningRankingValuesSchema = z.object({
  /** Numero minimo di partecipanti classificati perché le fasce paghino. */
  minParticipants: z.number().int().min(1).optional(),
}).strict();
export type CommissioningRankingValues = z.infer<typeof commissioningRankingValuesSchema>;
export const COMMISSIONING_RANKING_VALUE_FIELDS = ['minParticipants'] as const;
export type CommissioningRankingValueField = typeof COMMISSIONING_RANKING_VALUE_FIELDS[number];

/** Numeri di una sorgente dell'Aggregazione (la struttura è nel template). */
export const commissioningAggregationSourceValuesSchema = z.object({
  /** Peso della lettura della sorgente nel punteggio composto (≥ 0). */
  weight: z.number().finite().nonnegative().optional(),
}).strict();
export type CommissioningAggregationSourceValues = z.infer<typeof commissioningAggregationSourceValuesSchema>;
export const COMMISSIONING_AGGREGATION_SOURCE_VALUE_FIELDS = ['weight'] as const;
export type CommissioningAggregationSourceValueField = typeof COMMISSIONING_AGGREGATION_SOURCE_VALUE_FIELDS[number];

/** Numeri di un livello dell'Aggregazione (la struttura è nel template). */
export const commissioningAggregationLevelValuesSchema = z.object({
  /** Punteggio composto minimo per raggiungere il livello. */
  minScore: z.number().finite().optional(),
  /** Premio in euro del livello raggiunto. */
  payout: z.number().finite().optional(),
}).strict();
export type CommissioningAggregationLevelValues = z.infer<typeof commissioningAggregationLevelValuesSchema>;
export const COMMISSIONING_AGGREGATION_LEVEL_VALUE_FIELDS = ['minScore', 'payout'] as const;
export type CommissioningAggregationLevelValueField = typeof COMMISSIONING_AGGREGATION_LEVEL_VALUE_FIELDS[number];

export const commissioningThresholdValuesSchema = z.object({
  /** Valori per banda Soglie (chiave = id banda della struttura del template). */
  bands: z.record(z.string().uuid(), commissioningThresholdBandValuesSchema),
  /** Valori per regola Bonus/Malus su KPI (chiave = id regola della struttura del template). */
  rules: z.record(z.string().uuid(), commissioningBonusMalusRuleValuesSchema).optional(),
  /** Valori per fascia di posizione della Classifica (chiave = id fascia della struttura del template). */
  prizes: z.record(z.string().uuid(), commissioningRankingPrizeValuesSchema).optional(),
  /** Valori d'insieme della Classifica. */
  ranking: commissioningRankingValuesSchema.optional(),
  /** Valori per sorgente dell'Aggregazione (chiave = id sorgente della struttura del template). */
  sources: z.record(z.string().uuid(), commissioningAggregationSourceValuesSchema).optional(),
  /** Valori per livello dell'Aggregazione (chiave = id livello della struttura del template). */
  levels: z.record(z.string().uuid(), commissioningAggregationLevelValuesSchema).optional(),
}).strict().superRefine((values, ctx) => {
  if (Object.keys(values.bands).length > 100) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['bands'], message: 'Sono ammesse al massimo 100 bande',
  });
  if (Object.keys(values.rules ?? {}).length > 100) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['rules'], message: 'Sono ammesse al massimo 100 regole',
  });
  if (Object.keys(values.prizes ?? {}).length > 200) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['prizes'], message: 'Sono ammesse al massimo 200 fasce',
  });
  if (Object.keys(values.sources ?? {}).length > 100) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['sources'], message: 'Sono ammesse al massimo 100 sorgenti',
  });
  if (Object.keys(values.levels ?? {}).length > 100) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['levels'], message: 'Sono ammessi al massimo 100 livelli',
  });
});
export type CommissioningThresholdValues = z.infer<typeof commissioningThresholdValuesSchema>;

/** Campo valore richiesto da ogni effetto dichiarato di una regola. */
export const COMMISSIONING_BONUS_MALUS_EFFECT_FIELD = {
  malus: 'malusValue',
  bonus: 'bonusValue',
} as const satisfies Record<CommissioningBonusMalusEffect, CommissioningBonusMalusRuleValueField>;

/** Etichette operative (una sola fonte per BE, FE e documenti). */
export const COMMISSIONING_BONUS_MALUS_EFFECT_LABEL: Record<CommissioningBonusMalusEffect, string> = {
  malus: 'Malus sotto la banda',
  bonus: 'Bonus sopra la banda',
};
export const COMMISSIONING_BONUS_MALUS_WINDOW_LABEL: Record<CommissioningBonusMalusWindow, string> = {
  period: 'Periodo di gara',
  rolling_30d: 'Ultimi 30 giorni',
  rolling_90d: 'Ultimi 90 giorni',
};
export const COMMISSIONING_BONUS_MALUS_RULE_VALUE_LABEL: Record<CommissioningBonusMalusRuleValueField, string> = {
  deadBandLow: 'Banda morta da',
  deadBandHigh: 'Banda morta a',
  malusValue: 'Malus €',
  bonusValue: 'Bonus €',
};
export const COMMISSIONING_RANKING_PRIZE_VALUE_LABEL: Record<CommissioningRankingPrizeValueField, string> = {
  prizeValue: 'Premio €',
};
export const COMMISSIONING_RANKING_VALUE_LABEL: Record<CommissioningRankingValueField, string> = {
  minParticipants: 'Partecipanti minimi',
};
export const COMMISSIONING_AGGREGATION_SOURCE_VALUE_LABEL: Record<CommissioningAggregationSourceValueField, string> = {
  weight: 'Peso',
};
export const COMMISSIONING_AGGREGATION_LEVEL_VALUE_LABEL: Record<CommissioningAggregationLevelValueField, string> = {
  minScore: 'Punteggio minimo',
  payout: 'Premio €',
};

/** Campo valore associato a ogni fonte dichiarabile. */
export const COMMISSIONING_THRESHOLD_SOURCE_FIELD = {
  canone_multiplier: 'canoneMultiplier',
  gettone_gara_multiplier: 'gettoneGaraMultiplier',
  per_unit_eur: 'perUnitEur',
  flat_eur: 'flatEur',
  percent_of_amount: 'percentOfAmount',
} as const satisfies Record<CommissioningThresholdSource, keyof CommissioningThresholdBandValues>;
export type CommissioningThresholdSourceField =
  typeof COMMISSIONING_THRESHOLD_SOURCE_FIELD[CommissioningThresholdSource];

/** Etichette operative delle fonti (una sola fonte di verità per BE, FE e documenti). */
export const COMMISSIONING_THRESHOLD_SOURCE_LABEL: Record<CommissioningThresholdSource, string> = {
  canone_multiplier: 'Moltiplicatore canone',
  gettone_gara_multiplier: 'Moltiplicatore gettone gara',
  per_unit_eur: '€ per unità',
  flat_eur: 'Premio flat €',
  percent_of_amount: '% sull\'importo',
};
